import { Suspense } from 'react';

import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { getAccountStatus } from '@helpers/Accounts/getAccountStatus';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { Pill } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';

export const RCFStatusPill = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense
                fallback={
                    <Skeleton
                        className="h-[5px] w-[60px] rounded-full"
                        data-testid="rcf-status-pill-loading-skeleton"
                    />
                }
            >
                <StatusPill accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const StatusPill = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();

    const { data: account } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: ({ data }) =>
            data.accounts.find((account) => account.account_id === accountId),
        suspense: true
    });

    if (!account) {
        const errorMessage = `<RCFStatusPill /> - No account found with id: ${accountId}`;

        logError(errorMessage, {
            tags: TAGS.DE_LENDING
        });
        throw new Error(errorMessage);
    }

    const formalOfferAccessPeriod = account.formal_offer_access_period;

    if (!formalOfferAccessPeriod) {
        const errorMessage = `<RCFStatusPill /> - No formal offer access period found for account: ${accountId}`;

        logError(errorMessage, {
            tags: TAGS.DE_LENDING
        });
        throw new Error(errorMessage);
    }

    if (getAccountStatus(account) === 'overdue')
        return (
            <Pill variant="red">
                <FormattedMessage
                    id="_.dashboard.rcf_title_overdue"
                    defaultMessage="Overdue"
                />
            </Pill>
        );

    if (formalOfferAccessPeriod.withdrawals_paused) {
        return (
            <Pill variant="yellow" size="small">
                <FormattedMessage
                    id="_.dashboard.rcf_title_paused"
                    defaultMessage="Withdrawals paused"
                />
            </Pill>
        );
    }

    return (
        <Pill variant="green" size="small">
            <FormattedMessage
                id="_.dashboard.rcf_title_active"
                defaultMessage="active"
            />
        </Pill>
    );
};
